/**
 * PATH: packages/bills/src/billJournal.ts
 * FILE_ID: BILLS-PKG-JOURNAL-001
 * PURPOSE: Pure double-entry journal preview for a bill (p3c). Computes
 *          the balanced posting that an ACCPAY bill represents:
 *            DR expense per CoA line  (ex-GST)
 *            DR GST control            (input GST)
 *               CR Accounts Payable    (= invoice total)
 *          and reports whether Σdebit == Σcredit so the operator sees a
 *          mis-coded / un-reconciled bill BEFORE pushing to Xero.
 *
 * WHY: Xero stays the general ledger — Quotech does not post journals.
 *      But a bookkeeper coding a bill needs to see the journal it WILL
 *      become, and catch the case where the coded lines + GST don't add
 *      up to what the supplier is charging (the classic AP error).
 *
 * Pure: input (a BillDetail-ish shape) → output (a BillJournal). No I/O,
 * no React — unit-testable in isolation. The backend
 * ``GET /api/bills/<id>/journal-preview/`` computes the authoritative
 * version with the same model; this is the instant client-side mirror.
 *
 * DEPENDENCIES: ../types only.
 */

import type { BillDetail, BillLineItem } from './types';

export interface JournalLine {
  /** GL account code, or a label like '(uncoded)' / 'GST' / 'Accounts Payable'. */
  account: string;
  /** Optional human name when the CoA is known. */
  account_name?: string;
  description: string;
  debit: number;
  credit: number;
}

export interface BillJournal {
  lines: JournalLine[];
  total_debit: number;
  total_credit: number;
  /** abs(total_debit - total_credit) < 0.005. */
  balanced: boolean;
  /** total_debit - total_credit (signed; 0 when balanced). */
  variance: number;
  currency: string;
}

const UNCODED = '(uncoded)';

function num(v: unknown): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[,$\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Ex-GST amount of a line: prefer line_total, else unit×qty. */
function lineAmount(it: BillLineItem): number {
  const total = num(it.line_total);
  if (total) return total;
  const qty = num(it.quantity) || 1;
  return round2(num(it.unit_amount) * qty);
}

/**
 * Build the balanced journal preview for a bill.
 *
 * ``opts.accountName`` (optional) resolves a CoA code → display name so
 * the preview reads "310 · Materials" instead of a bare code.
 */
export function buildBillJournal(
  bill: Partial<
    Pick<
      BillDetail,
      'line_items' | 'gst' | 'total' | 'currency' | 'extra_charge_amount' | 'extra_charge_name'
    >
  >,
  opts: { accountName?: (code: string) => string | undefined } = {},
): BillJournal {
  const lines: JournalLine[] = [];

  // 1) Expense debits — group lines by GL account code (ex-GST).
  const byAccount = new Map<string, number>();
  for (const raw of bill.line_items ?? []) {
    if (!raw || typeof raw !== 'object') continue;
    const code = String(raw.account_code ?? raw.AccountCode ?? '').trim() || UNCODED;
    byAccount.set(code, round2((byAccount.get(code) ?? 0) + lineAmount(raw)));
  }

  // 2) Extra charge (freight/shipping) — folded into the uncoded bucket
  //    as a discrete debit, mirroring the Xero push which appends it as
  //    its own line. The freight bucket → GL code mapping is resolved
  //    server-side, so on the FE it lands as uncoded expense.
  const extra = num(bill.extra_charge_amount);
  if (extra > 0) {
    byAccount.set(UNCODED, round2((byAccount.get(UNCODED) ?? 0) + extra));
  }

  for (const [code, amount] of byAccount) {
    lines.push({
      account: code,
      account_name: code === UNCODED ? undefined : opts.accountName?.(code),
      description: code === UNCODED ? 'Uncoded expense lines' : 'Expense',
      debit: round2(amount),
      credit: 0,
    });
  }

  // 3) GST control debit (input GST recoverable).
  const gst = round2(num(bill.gst));
  if (gst > 0) {
    lines.push({
      account: 'GST',
      account_name: 'GST on Expenses',
      description: 'Input GST (recoverable)',
      debit: gst,
      credit: 0,
    });
  }

  // 4) Accounts Payable credit — what we owe the supplier (invoice total).
  const apCredit = round2(num(bill.total));
  lines.push({
    account: 'Accounts Payable',
    account_name: 'AP control',
    description: 'Amount owed to supplier',
    debit: 0,
    credit: apCredit,
  });

  const total_debit = round2(lines.reduce((s, l) => s + l.debit, 0));
  const total_credit = round2(lines.reduce((s, l) => s + l.credit, 0));
  const variance = round2(total_debit - total_credit);

  return {
    lines,
    total_debit,
    total_credit,
    balanced: Math.abs(variance) < 0.005,
    variance,
    currency: bill.currency || 'AUD',
  };
}
